import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Footer: React.FC = () => {
  const companyName = 'Sightline UAV';
  const currentYear = new Date().getFullYear();
  const companyEmail = 'info@talinvestments.net';
  const companyPhone = '+972 54 4707685';
  const companyAddress = 'Herzelia Technology Park, Herzelia, Israel';

  const navigationLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <footer className="bg-zinc-900 text-white py-16 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <img 
              src="/assets/logo.png" 
              alt="Sightline UAV Logo" 
              className="h-12 w-auto mb-4"
            />
            <h3 className="font-bold text-2xl mb-4 text-blue-400">{companyName}</h3>
            <p className="text-zinc-300 mb-4">
              Professional drone services led by former IDF specialists. Precision excellence for every mission.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="font-semibold text-lg mb-4">Navigation</h4>
            <nav className="space-y-2">
              {navigationLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-zinc-300 hover:text-blue-400 transition-colors duration-200 block"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <div className="space-y-3">
              <a
                href={`mailto:${companyEmail}`}
                className="flex items-center gap-3 text-zinc-300 hover:text-blue-400 transition-colors duration-200"
              >
                <Mail className="w-5 h-5" />
                <span>{companyEmail}</span>
              </a>
              <a
                href={`tel:${companyPhone}`}
                className="flex items-center gap-3 text-zinc-300 hover:text-blue-400 transition-colors duration-200"
              >
                <Phone className="w-5 h-5" />
                <span>{companyPhone}</span>
              </a>
              <div className="flex items-start gap-3 text-zinc-300">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>{companyAddress}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-zinc-700 my-8"></div>

        {/* Bottom Section */}
        <motion.div
          className="flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="text-zinc-400 text-sm">
            © {currentYear} {companyName}. All rights reserved.
          </div>
          
          <div className="flex items-center gap-6">
            {/* Social Media Links */}
            {[
              { name: 'LinkedIn', href: 'https://linkedin.com' },
              { name: 'Facebook', href: 'https://facebook.com' },
              { name: 'Twitter', href: 'https://twitter.com' },
            ].map((social) => (
              <a
                key={social.name}
                href={social.href}
                className="text-zinc-400 hover:text-blue-400 transition-colors duration-200 text-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                {social.name}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.p
          className="text-center text-zinc-500 text-sm mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Elevating Industries Through Precision Aerial Technology
        </motion.p>
      </div>
    </footer>
  );
};
